import { Label, Table } from 'reactstrap';
import { BlueHorizontalLine } from '~/shared/components/icons';
import { toUSD } from '~/shared/utils';
import moment from 'moment';
import { Fragment, useContext, useState } from 'react';
import { FundRequestContext } from '~/context/fundrequests';
import { tr } from 'date-fns/locale';
import { MdOutlineReceiptLong } from 'react-icons/md';
import { MdCheckCircle } from 'react-icons/md';
import { MdWarning } from 'react-icons/md';
import { FileViewerModal } from './FileViewerModal';

const ExpenseTable = () => {
    const [{ fundRequest }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [selectedFile, setSelectedFile] = useState<{url: string, filename: string} | null>(null);
    const [fileList, setFileList] = useState<Array<{url: string, filename: string}>>([]);
    const [fileIndex, setFileIndex] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleFileClick = (
        uploads: Array<{ image_url: string; filename: string }>,
        startIndex: number = 0
    ) => {
        if (!uploads || uploads.length === 0) return;
        const files = uploads.map((u) => ({ url: u.image_url, filename: u.filename }));
        setFileList(files);
        setFileIndex(Math.min(Math.max(startIndex, 0), files.length - 1));
        setSelectedFile(files[Math.min(Math.max(startIndex, 0), files.length - 1)]);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedFile(null);
    };

    const {
        transactions_fundraising,
        transactions_medical,
        transactions_medications,
        transactions_insurance,
        transactions_relocation_lodging,
        transactions_other,
        transactions_travel,
        transactions_food,
        transactions_caregiving,
        transactions_physical_therapy,
    } = fundRequest;

    const defaultTotal = transactions_medical?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );

    const travelTotal = transactions_travel?.reduce(
        (acc, item) => acc + (item.amount ? Number(item.amount) : 0),
        0
    );

    const foodTotal = transactions_food?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const fundraisingTotal = transactions_fundraising?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const careTotal = transactions_caregiving?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const physicalTotal = transactions_physical_therapy?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const medicationTotal = transactions_medications?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const insuranceTotal = transactions_insurance?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const relocationTotal = transactions_relocation_lodging?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const otherTotal = transactions_other?.reduce(
        (acc, item) => acc + Number(item.amount),
        0
    );
    const grandTotal =
        defaultTotal +
        travelTotal +
        foodTotal +
        fundraisingTotal +
        medicationTotal +
        insuranceTotal +
        relocationTotal +
        careTotal +
        physicalTotal +
        otherTotal;

    const getCategoryTotal = (category: string) => {
        switch (category) {
            case 'Fundraising':
                return fundraisingTotal;
            case 'Medical':
                return defaultTotal;
            case 'Medications':
                return medicationTotal;
            case 'Travel/Mileage':
                return travelTotal;
            case 'Food/Incidentals':
                return foodTotal;
            case 'Insurance':
                return insuranceTotal;
            case 'Relocation/Lodging':
                return relocationTotal;
            case 'Caregiving':
                return careTotal;
            case 'Physical Therapy':
                return physicalTotal;
            case 'Other':
                return otherTotal;
            default:
                return 0;
        }
    };

    const leftAlign = {
        padding: 0,
        paddingTop: 10,
        textAlign: 'left',
        background: 'transparent',
    };
    const rightAlign = {
        padding: 0,
        paddingTop: 10,
        textAlign: 'right',
        background: 'transparent',
    };

    const expenseSection = (category, transactions) => {
        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Label className='mb-0' style={{ fontSize: '1rem' }}>
                        {category}
                    </Label>
                    <Label className='mb-0' style={{ fontSize: '1rem' }}>
                        {toUSD(getCategoryTotal(category))}
                    </Label>
                </div>
                <BlueHorizontalLine />
                <Table borderless>
                    <tbody>
                        {transactions.map((item, i) => {
                            const expenseDate =
                                item?.expense_date ||
                                item?.date_of_travel ||
                                item?.start_date;
                            const description =
                                item?.description ||
                                item?.recipient_name ||
                                item?.destination;

                            return (
                                <Fragment key={`expense-${category}-${i}`}>
                                    <tr>
                                        <td style={leftAlign}>{description}</td>
                                        <td style={rightAlign}>
                                            {moment(expenseDate).format(
                                                'MM/DD/YYYY'
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                ...rightAlign,
                                                width: '100px',
                                            }}
                                        >
                                            {toUSD(item.amount)}
                                        </td>
                                    </tr>
                                    {/* Display all documents for this expense in a separate row */}
                                    {item?.uploads && item?.uploads?.length > 0 && (
                                        <tr>
                                            <td colSpan={3} style={{ 
                                                padding: '8px 0 16px 0',
                                                background: 'transparent',
                                                textAlign: 'left'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '4px',
                                                    fontSize: '14px',
                                                    paddingLeft: '0'
                                                }}>
                                                    {item.uploads.map((upload, uploadIndex) => (
                                                        <div 
                                                            key={`${i}-upload-${uploadIndex}`}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                cursor: 'pointer',
                                                                padding: '4px 0',
                                                                color: '#01aeb4',
                                                            }}
                                                            onClick={() => handleFileClick(item.uploads, uploadIndex)}
                                                        >
                                                            <MdOutlineReceiptLong
                                                                size={20}
                                                                style={{ flexShrink: 0 }}
                                                            />
                                                            <span style={{
                                                                wordBreak: 'break-word'
                                                            }}>
                                                                {upload.filename}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {/* Display flagged comment - pending (yellow) or resolved (green) */}
                                    {item?.flagged_comment && (
                                        <tr>
                                            <td colSpan={3} style={{ 
                                                padding: '8px 0',
                                                background: 'transparent'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    backgroundColor: item?.flagged_resolved_date ? '#f8f9fa' : '#FEF3C7',
                                                    borderRadius: '4px',
                                                    border: item?.flagged_resolved_date ? '1px solid #e9ecef' : '1px solid #FCD34D'
                                                }}>
                                                    {item?.flagged_resolved_date ? (
                                                        <MdCheckCircle 
                                                            size={18} 
                                                            style={{ 
                                                                color: '#28a745',
                                                                flexShrink: 0
                                                            }} 
                                                        />
                                                    ) : (
                                                        <MdWarning 
                                                            size={18} 
                                                            style={{ 
                                                                color: '#D97706',
                                                                flexShrink: 0
                                                            }} 
                                                        />
                                                    )}
                                                    <span style={{
                                                        textDecoration: item?.flagged_resolved_date ? 'line-through' : 'none',
                                                        color: item?.flagged_resolved_date ? '#6c757d' : '#92400E',
                                                        fontSize: '14px',
                                                        flex: 1
                                                    }}>
                                                        {!item?.flagged_resolved_date && <strong>Pending: </strong>}
                                                        {item.flagged_comment}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </Table>
                <BlueHorizontalLine />
            </div>
        );
    };

    return (
        <>
            {transactions_fundraising.length > 0 &&
                expenseSection('Fundraising', transactions_fundraising)}
            {transactions_medical.length > 0 &&
                expenseSection('Medical', transactions_medical)}
            {transactions_medications.length > 0 &&
                expenseSection('Medications', transactions_medications)}
            {transactions_travel.length > 0 &&
                expenseSection('Travel/Mileage', transactions_travel)}
            {transactions_caregiving.length > 0 &&
                expenseSection('Caregiving', transactions_caregiving)}
            {transactions_physical_therapy.length > 0 &&
                expenseSection(
                    'Physical Therapy',
                    transactions_physical_therapy
                )}
            {transactions_food.length > 0 &&
                expenseSection('Food/Incidentals', transactions_food)}
            {transactions_insurance.length > 0 &&
                expenseSection('Insurance', transactions_insurance)}
            {transactions_relocation_lodging.length > 0 &&
                expenseSection(
                    'Relocation/Lodging',
                    transactions_relocation_lodging
                )}
            {transactions_other.length > 0 &&
                expenseSection('Other', transactions_other)}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Label style={{ fontSize: '1rem' }}>Total Requested</Label>

                <strong>{toUSD(grandTotal)}</strong>
            </div>

            <FileViewerModal
                isOpen={isModalOpen}
                toggle={closeModal}
                fileUrl={selectedFile?.url || ''}
                filename={selectedFile?.filename || ''}
                files={fileList}
                currentIndex={fileIndex}
            />
        </>
    );
};

export default ExpenseTable;
